import rawBalance from "../../../config/balance.json";

/** Keys starting with "$" are notes for people editing the file, not game data (a list of missiles once showed its note as a missile). */
function withoutNotes<T>(v: T): T {
  if (Array.isArray(v)) return v.map(withoutNotes) as T;
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).filter(([k]) => !k.startsWith("$")).map(([k, x]) => [k, withoutNotes(x)])) as T;
  return v;
}
const balance = withoutNotes(rawBalance);

/**
 * Where the world server is. "same-origin" (or empty) means this site is served by the world
 * server itself, as in the online preview.
 */
function worldServerUrl(): string {
  const v = process.env.NEXT_PUBLIC_WORLD_SERVER_URL ?? "http://localhost:4000";
  if (v === "" || v === "same-origin") return typeof window !== "undefined" ? window.location.origin : "";
  return v.replace(/\/$/, "");
}

/** Public settings (inlined at build time). Defaults are free services that need no key. */
export const settings = {
  worldServer: worldServerUrl(),
  openFreeMapTileJson: process.env.NEXT_PUBLIC_OPENFREEMAP_TILEJSON ?? "https://tiles.openfreemap.org/planet",
  elevationTiles: process.env.NEXT_PUBLIC_ELEVATION_TILES ?? "https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png",
  glyphs: "https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf",
  geocoder: "https://photon.komoot.io/api/",
};

/** MapLibre's ES modules, served from public/ (copied there by scripts/copy-maplibre.mjs). */
export const MAPLIBRE_DIR = `/maplibre/${process.env.NEXT_PUBLIC_MAPLIBRE_VERSION}`;

/** From config/balance.json (the web app reads the file directly to keep the bundle small). */
export const COLORS: string[] = balance.start.colors;
export const START = { ...balance.start.resources, units: balance.start.units, maxTownPopulation: balance.start.maxTownPopulation };
export const NAME_LIMITS = balance.names;
/** Half the starting kit for a leader starting again after losing everything. */
export const RESTART_SHARE: number = balance.start.restartFraction;
export const BARRACKS = { cost: balance.buildings.barracks.cost as Record<string, number>, minutes: balance.buildings.barracks.buildMinutes };
/** Buildings and their level-1 cost and time; level L costs and takes L times as much (economy.buildingLevels). */
export const BUILDINGS = balance.buildings as unknown as Record<string, {
  cost: Record<string, number>; buildMinutes: number; workers: number;
  farmlandKm2PerLevel?: number; foodPerFarmlandKm2PerHour?: number; oilPerHour?: number; minOil?: number; steelPerHour?: number; oilUsePerHour?: number;
  maxLevel?: number; notOn?: string[]; coastOnly?: boolean; needsPower?: boolean; aircraftPerRunway?: number; rangeMiles?: number;
  rangeMilesPerLevel?: number; shootDownPerLevel?: number; maxShootDown?: number; withoutRadar?: number; tradePerHour?: number;
  worksHp?: number; defenseBonus?: number; infantryDefenseBonus?: number; infantryAttackPenalty?: number; maxPenalty?: number;
  firePower?: number; artilleryDefenseBonus?: number; missilesPerLevel?: number;
  helicoptersPerLevel?: number; railSpeedMultiplier?: number; oilBonus?: number;
  happinessPerLevel?: number; growthPerLevel?: number; protected?: boolean; feedSharePerLevel?: number; workersPerFarmlandKm2?: number; maxBuildHours?: number;
  landKm2PerStep?: number; steelPerKm2PerHour?: number; workersPerKm2?: number;
}>;
export const MAX_LEVEL: number = balance.economy.buildingLevels.max;
/** The highest level a building goes to. */
export const maxLevelOf = (type: string): number => BUILDINGS[type]?.maxLevel ?? MAX_LEVEL;
export const TAX = {
  min: balance.economy.taxRateMin, max: balance.economy.taxRateMax, normal: balance.economy.taxRateDefault,
  perPerson: balance.economy.taxPerPersonPerHour, happinessPerRate: balance.economy.happiness.perTaxRate,
};
export const MILITARY_SHARE_LIMIT: number = balance.economy.happiness.military.spendingShareThreshold;
/** Asked: people join up on their own above this happiness, more the happier they are. */
export const ENLIST = balance.economy.enlist as { unit: string; minHappiness: number; maxPerHour: number };
export const INFANTRY = { money: balance.units.infantry.cost.money, minutes: balance.units.infantry.buildMinutes, per: balance.units.infantry.buildPer };

/** Units (SPEC: Units): cost, crew, build time and where each is made. */
export const UNITS = balance.units as unknown as Record<string, {
  cost: Record<string, number>; crew: number; wagesPerHour: number; attack: number; defense: number; hp: number; buildMinutes: number; buildPer?: number;
  speed: string; builtAt: string; carriesInfantry?: number; carriesTanks?: number;
}>;
/** Which units each building makes, in the config's order. */
export const MADE_AT: Record<string, string[]> = Object.entries(UNITS).reduce((acc, [k, u]) => {
  (acc[u.builtAt] ??= []).push(k);
  return acc;
}, {} as Record<string, string[]>);
export const unitClass = (type: string): "land" | "air" | "sea" => (UNITS[type]?.builtAt === "airbase" ? "air" : UNITS[type]?.builtAt === "port" ? "sea" : "land");
/** Air missions: how far each aircraft reaches, and how far recon sees. */
export const AIR = { rangeMiles: balance.air.rangeMiles as Record<string, number>, reconSightMiles: balance.air.reconSightMiles, repairMinutesPerHit: balance.air.repairMinutesPerHit };
export const RADAR_MILES: number = balance.buildings.radar.rangeMiles;
export const POWER_MILES_PER_LEVEL: number = balance.buildings.powerPlant.rangeMilesPerLevel;
export const FUEL_PER_100_MILES = balance.fuelOilPer100Miles as unknown as Record<string, number>;


/** What each kind of weather does: how much longer marches take, and how well jets and tanks fight (SPEC: Weather). */
export const WEATHER: Record<string, { movement: number; jets: number; tanks: number }> = balance.weather.types;

/** The world market and trade (Milestone 8). */
export const MARKET = balance.market;
export const TRADE = balance.trade;

/** Fortifications (Milestone 10) and missiles. */
export const FORT_TYPES: string[] = balance.fortifications.types;
export const REPAIR_PER_HOUR: number = balance.fortifications.repairPerHour;
/** Supply (Milestone 11): strength cut off, fuel and ammo, comms, engineers' repairs. */
export const SUPPLY = balance.supply;
export const MISSILE = balance.missiles.cruiseMissile as { cost: Record<string, number>; buildMinutes: number; builtAt: string };

/** Freeform building (Milestone 14). */
export const BLOCKS = balance.blocks as unknown as {
  chunkSize: number; buildRadiusM: number; heightLimitM: number; maxHeightM: number; maxOpsPerRequest: number; refundShare: number;
  materials: Record<string, { id: number; cost: Record<string, number>; hp: number; color: string; needs?: string; burns?: boolean }>;
};

/** Milestone 15: strike weapons, damage states and rebuilding. */
export const STRIKES = balance.strikes as unknown as {
  weapons: Record<string, { rangeMiles: number; blastFt: number; from: "garrison" | "missileSite" | "airbase"; unit?: string; crew?: number; cost?: Record<string, number>; counter: string; mph?: number; timerMinutes?: number }>;
  maxRounds: number;
  rebuildCostShare: number;
  rebuildMinutesShare: number;
};
export const MISSILES = balance.missiles as unknown as Record<string, { cost: Record<string, number>; buildMinutes: number; builtAt: string }>;
export const WEAPON_LABELS: Record<string, string> = {
  artillery: "Artillery", mortar: "Mortar team", drone: "Drone", cruiseMissile: "Cruise missile", ballisticMissile: "Ballistic missile",
  navalGuns: "Naval guns", demolition: "Demolition charge", airStrike: "Air strike",
};
export const COUNTER_LABELS: Record<string, string> = {
  counterBattery: "counter-battery fire from their artillery", infantry: "their infantry", antiAir: "anti-air (better with radar)",
  jamming: "jamming from comms towers, and anti-air", missileDefense: "missile defense only", coastalGuns: "coastal guns (artillery positions)", garrison: "the troops there",
};

/** Ads (asked: AdSense in a corner): off until the publisher ID and ad slot are set in config/balance.json. */
export const ADS = { client: balance.site.adsensePublisher, slot: balance.site.adsenseSlot, on: !!(balance.site.adsensePublisher && balance.site.adsenseSlot) };
/** Google checks the site with its code on every page (from the publisher ID alone, before any ad shows). */
export const ADS_VERIFY = !!balance.site.adsensePublisher;
